/* eslint-disable camelcase */
/* eslint-disable no-use-before-define */
import { Landing } from 'modules/dashboard/types/CustomDashboard';
import { Beneficiary } from './Beneficiary';
import { Document } from './Document';
import { Operation } from './Operation';
import { Partner } from './Partner';
import { Share } from './Share';
import { Padding } from './Padding';

export type Society = {
  _id: string;
  name: string;
  cif: string;
  web: string;
  logo: string;
  phone: string;
  creationDate: Date;
  constitutionDate: Date;
  legalAddress: {
    line1: string;
    zip: string;
    city: string;
    country: string;
  };
  additional: SocietyAdditional;
  language: string;
  societyLanguage: string;
  societyType: SocietyTypes;
  activePartners: number;
  corporateId: SocietyCorporateId;
  currentPartner: number;
  currentBeneficiary: number;
  currentInvestor: number;
  nominalValue: number;
  annualValues: {
    year: string;
    contableValue: number;
    billing: number;
    ebitda: number[];
    modifiedDate: Date;
  }[];
  sharesCount: {
    actual: number;
    future: number;
    total?: number;
    drafts: number;
  };
  mainAdmin: string;
  documents: Document[];
  shares: Share[];
  operations: Operation[];
  stockPlans: Record<string, any>[];
  drafts: Record<string, any>[];
  tenderOffers: Record<string, any>[];
  boards: Record<string, any>[];
  partners: Partner[];
  beneficiaries: Beneficiary[];
  investors: Record<string, any>[];
  administrators: Record<string, any>[];
  guests: Record<string, any>[];
  employees: Record<string, any>[];
  employeesPlans: Record<string, any>[];
  sendNotifications: boolean;
  societyValues: SocietyValue[];
  minutebookFolders: { label: string }[];
  myFolders: { label: string }[];
  councilMinutes: { label: string }[];
  mainDirector: string;
  directors: SocietyDirector[];
  representatives: SocietyRepresentative[];
  auditors: SocietyAuditor[];
  communications: Record<string, any>[];
  updateSocietyValueND: boolean;
  updateSocietyValueFD: boolean;
  integrations: {
    factorial: {
      enabled: boolean;
      key: string;
      token: string;
      companyId: Number;
      validated: boolean;
      validatedAt: Date;
    };
    personio: {
      enabled: boolean;
      key: string;
      token: string;
      validated: boolean;
      validatedAt: Date;
    };
  };
  isSocietyHolding: boolean;
  isSocietyDemo: boolean;
  hasAccessCaptable: boolean;
  hasShareTags: boolean;
  sharesCertificateTemplate: string;
  demos: Record<string, any>[];
  customization: SocietyCustomization;
  configuration: SocietyConfiguration;
  metadata: SocietyAdditional;
  templates: SocietyTemplates;
  sharesTitlesCount?: {
    active: number;
    total: number;
  };
  partnersCount?: {
    active: number;
    total: number;
    usufructuaries: number;
  };

  operationsCount?: {
    total: number;
    constitution: number;
    capitalIncrease: number;
    capitalDecrease: number;
    sellParticipation: number;
    heritage: number;
    donation: number;
    endorsement: number;
    split: number;
    renumeration: number;
    reclassification: number;
    dividendsDistribution: number;
    contribution: number;
  };

  holdingClassesCount: {
    total: number;
  };

  communicationsCount?: {
    total: number;
    unread: number;
  };

  foldersCount?: {
    total: number;
  };
};

export type SocietyChart = {
  holdingId: string;
  holdingName: string;
  holdingCIF: string;
  holdingLogo: string;
  holdingShares: number;
  holdingPercentND: number | null;
  holdingPercentFD: number | null;
  holdingReference: string[] | null;
  holdingReferenceLevel: number;
  holdingKey: string;
  holdingValue?: number;
  holdingPartners?: any;
  holdingEmail?: string;
  holdingUserType?: string;
};

export type SocietyTheme = {
  logo: string;
  frameColor: string;
  headingsColor: string;
  bodyTextColor: string;
  linkColor: string;
  buttonColor: string;
  buttonTextColor: string;
  buttonRounded: boolean;
};

export type SocietyThemeHeaderOptions = {
  label: string;
  type: string;
  url: string;
};

export type SocietyCustomization = {
  theme: {
    logo: string;
    logoHref: string;
    headerFullWidth: boolean;
    headerHeight: string;
    headerColor: string;
    headerTextColor: string;
    headerTextHoverColor: string;
    headerOptions: SocietyThemeHeaderOptions[];
    emailFrameColor: string;
    emailFrameLogo: string;
    emailHeadingsColor: string;
    emailBodyTextColor: string;
    emailLinkColor: string;
    emailButtonColor: string;
    emailButtonTextColor: string;
    buttonRounded: boolean;
  };
};

export type NotificationFrequency =
  | 'DAILY'
  | 'WEEKLY'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'YEARLY'
  | 'ON_EVENT';

export type SocietyNotificationConfig = {
  enabled: boolean;
  frequency?: NotificationFrequency;
};

export type SocietyConfiguration = {
  decimalScale: number;
  hasWhiteBranding: boolean;
  hasCustomLanding?: boolean;
  hasLoginRedirect?: boolean;
  hasOldPartnersOn?: boolean;
  hasIncentiveChart?: boolean;
  hasThirdPartyIntegrations?: boolean;
  hasPartnerSharesRangeDisabled?: boolean;
  hasPartnerDocumentsTabDisabled?: boolean;
  hasBeneficiaryStrikeDisabled?: boolean;
  hasBeneficiaryPercentDisabled?: boolean;
  hasCouncilsV2Enabled?: boolean;
  hasSEPAOption?: boolean;
  hasVideoOnBoardsEnabled?: boolean;
  communications?: SocietyCommunicationsConfiguration;
  customLanding?: Landing;
  sharesTitles?: {
    enabled: boolean;
    initialNumber: number;
    isLandscape: boolean;
    backgroundImage: string;
    textPadding: Padding;
    isPartnerAllowedToDownload: boolean;
    showDatesAfter: string | Date;
  };
  equityMarketStatus?: SocietyEquityMarketStatus;
  notifications?: {
    enabled: boolean;
    incentivePlanVesting?: SocietyNotificationConfig;
    boardPositionExpiration?: SocietyNotificationConfig;
    employeeStatusChange?: SocietyNotificationConfig;
    societyAdministratorRegistration?: SocietyNotificationConfig;
    partnerShareholderRegistration?: SocietyNotificationConfig;
    annualMeetingsCount?: SocietyNotificationConfig;
    quarterlyCouncilsCount?: SocietyNotificationConfig;
    accountsFormulation?: SocietyNotificationConfig;
    annualAccountsApproval?: SocietyNotificationConfig;
  };
};

export type SocietyEmailTheme = {
  logo: string;
  frameColor: string;
  headingsColor: string;
  bodyTextColor: string;
  linkColor: string;
  buttonColor: string;
  buttonTextColor: string;
  buttonRounded: string;
};

export type SocietyCommunicationsConfiguration = {
  senderName: string;
  senderEmail: string;
  senderDomain?: string;
  replyEnabled: boolean;
  replyEmail: string;
  customLogo: string;
  customBackgroundColor: string;
  hasWhiteBranding: boolean;
  emailSignatureEnabled: boolean;
  emailSignatureHTML: string;
  emailSignature?: SocietyEmailSignature;
  theme: SocietyEmailTheme;
};

export type SocietyEmailSignature = {
  name: string;
  jobTitle: string;
  logo: string;
  website: string;
  phone: string;
  address: string;
  footer: string;
};

export const enum SocietyTypes {
  SL = 'SL', // eslint-disable-line
  SA = 'SA', // eslint-disable-line
  COOP = 'COOP', // eslint-disable-line
  ASSOC = 'ASSOC', // eslint-disable-line
  OTHER = 'OTHER', // eslint-disable-line
  SA_AXA = 'SA_AXA', // eslint-disable-line
}

export const enum SocietyEquityMarketStatus {
  OPEN = 'OPEN', // eslint-disable-line
  CLOSED = 'CLOSED', // eslint-disable-line
  UNAVAILABLE = 'UNAVAILABLE', // eslint-disable-line
}

export type SocietyPermissions = {
  customLanding: { enabled: boolean };
  dashboard: { enabled: boolean };
  partners: { enabled: boolean };
  portfolio: { enabled: boolean };
  drafts: { enabled: boolean };
  plans: { enabled: boolean };
  transactions: { enabled: boolean };
  communications: { enabled: boolean };
  boards: { enabled: boolean };
  documents: { enabled: boolean };
  societyConfiguration: { enabled: boolean };
};

export type SocietyValue = {
  date: Date;
  value: number;
  shareValue: number;
  isPriceByShare: boolean;
  futureValues?: number[];
};

export type SocietyDirectorType =
  | 'UNIQUE_ADMIN'
  | 'POOLED_ADMIN'
  | 'COUNSELOR'
  | 'DELEGATE_COUNSELOR'
  | 'COUNCIL_SECRETARY'
  | 'INDEPENDENT_COUNSELOR'
  | 'NON_EXECUTIVE_COUNSELOR'
  | 'JOINT_DELEGATE_COUNSELOR'
  | 'PROPRIETARY_COUNSELOR'
  | 'COUNCIL_PRESIDENT'
  | 'NON_COUNSELOR_VOWEL'
  | 'SOLIDARY_ADMIN'
  | 'SECRETARY_NO_DIRECTOR'
  | 'COUNCIL_VICE_PRESIDENT'
  | 'NON_DIRECTOR_DEPUTY_SECRETARY'
  | 'AUDITOR'
  | 'TREASURER'
  | 'OBSERVER';

export type SocietyDirector = {
  _id?: string;
  socialDenomination: string;
  cif: string;
  nationality: string;
  image: string;
  address: string;
  birthDate: string;
  initDate: string;
  endDate: string;
  condition: string;
  email: string;
  directorType: string;
  directorStatus: string;
  documents: string[];
  isNaturalPerson: boolean;
  representative: {
    name: string;
    cif: string;
    email: string;
    address: string;
    nationality: string;
  };
};

export type SocietyAuditor = {
  _id?: string;
  socialDenomination: string;
  cif: string;
  nationality: string;
  image?: string;
  address: string;
  email: string;
  initDate: string;
  endDate: string;
  auditorStatus: string;
  documents: string[];
};

export type SocietyRepresentative = {
  _id: string;
  address: string;
  comments: string;
  documents: string[];
  email: string;
  endDate: string;
  initDate: string;
  nationality: string;
  image?: string;
  nif: string;
  representativeStatus: string;
  representativeType: string;
  socialDenomination: string;
  tag: string;
  maximumAmount: number;
  notarialRegistration?: {
    notaryName: string;
    notaryCity: string;
    protocolNr: string;
  };
};

export type SocietyAdditional = {
  logo: string;
  description: string;
  bankAccountNumber: string;
  bankIdentifier: string;
  certificateSign: string;
  commercialRegister: {
    city: string;
    volume: string;
    folio: string;
    sheet: string;
    inscription: string;
  };
  identificationNumbers?: { key: string; value: string }[];
  incorporation?: string;
  fiscalYear?: string | null;
  notary: {
    name: string;
    city: string;
  };
  reservedMatters?: string;
  [key: string]: any;
};

export type SocietyTemplates = {
  sharesTitle?: string;
  sharesTitleWithCoOwners?: string;
  dividends?: string;
  tax2024?: string;
  tax2025?: string;
  sharesOwnershipCertificateES?: string;
  sharesOwnershipCertificateCA?: string;
  sharesOwnershipCertificateFooter?: string;
  sharesOwnershipCertificateES_2024?: string;
  sharesOwnershipCertificateCA_2024?: string;
  sharesOwnershipCertificateFooter_2024?: string;
  sharesOwnershipCertificateES_2025?: string;
  sharesOwnershipCertificateCA_2025?: string;
  sharesOwnershipCertificateFooter_2025?: string;
  capitalHoldingCertificateES?: string;
  capitalHoldingCertificateCA?: string;
  capitalHoldingCertificateFooter?: string;
  irpfCertificateES?: string;
  irpfCertificateFooter?: string;
  equityMarketPurchaseContract?: string;
};

export const enum SocietyCorporateId {
  AXA = 'axa', // eslint-disable-line
  ASG = 'asg', // eslint-disable-line
  DEFAULT = 'default', // eslint-disable-line
}
